import React, { useEffect } from "react";
import {
  IconMicrophone,
  IconMicrophoneOff,
  IconLoader,
} from "@tabler/icons-react";
import TalkingAvatar from "./TalkingAvatar";
import { getThemeValue } from "../hooks/theme";
import type { ThemeConfig } from "../store/types";
import { getStrings, Language } from "../i18n/strings";
import {
  injectPulseAnimations,
} from "../utils/pulseAnimations";

interface VoiceButtonProps {
  state: "idle" | "connecting" | "recording" | "disabled";
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  isEmbedded?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  theme?: Partial<ThemeConfig>;
  language?: Language;
}

const VoiceButton: React.FC<VoiceButtonProps> = ({
  state,
  onClick,
  onKeyDown,
  isEmbedded = false,
  className = "",
  style = {},
  children,
  theme,
  language,
}) => {
  const t = getStrings(language);
  // Inject pulse animation styles and custom styles
  useEffect(() => {
    injectPulseAnimations("voice-button-pulse-animations");

    const styleId = "voice-button-custom-styles";
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.textContent = `
        @keyframes voice-button-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .voice-button--recording {
          animation: avatar-button-pulse-blue 2s infinite;
        }

        .voice-button--connecting .voice-button__icon {
          animation: voice-button-spin 1s linear infinite;
        }
      `;
      document.head.appendChild(styleElement);
    }
  }, []);

  const getAriaLabel = () => {
    switch (state) {
      case "connecting":
        return t.statusConnecting;
      case "recording":
        return t.voiceStopRecording;
      case "disabled":
        return t.fallbackName;
      default:
        return t.voiceStartRecording;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
    onKeyDown(event);
  };

  const resolveButtonSize = (): string => {
    // Admin / theme launcher size wins. Without an explicit theme size,
    // embedded embeds stay compact; free-floating uses defaultTheme.buttonSize.
    if (theme?.buttonSize) return theme.buttonSize;
    if (isEmbedded) return "48px";
    return getThemeValue(theme, "buttonSize");
  };

  const getButtonStyles = (): React.CSSProperties => {
    const buttonSize = resolveButtonSize();
    const baseStyles: React.CSSProperties = {
      width: buttonSize,
      height: buttonSize,
      borderRadius: "50%",
      border: "none",
      cursor: state === "disabled" ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      position: "relative",
      fontFamily: getThemeValue(theme, "fontFamily"),
      outline: "none",
      opacity: state === "disabled" ? 0.5 : 1,
      ...style,
    };

    switch (state) {
      case "recording":
        return {
          ...baseStyles,
          background: "#ef4444",
          boxShadow: "0 4px 20px rgba(239, 68, 68, 0.4)",
          transform: "scale(1.1)",
        };
      case "connecting":
        return {
          ...baseStyles,
          background: "#f59e0b",
          boxShadow: "0 4px 20px rgba(245, 158, 11, 0.4)",
        };
      case "disabled":
        return {
          ...baseStyles,
          background: "linear-gradient(45deg, #9ca3af, #6b7280)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        };
      default:
        return {
          ...baseStyles,
          background: getThemeValue(theme, "buttonBackground"),
          boxShadow: `0 4px 20px ${getThemeValue(theme, "primaryColor")}40`,
        };
    }
  };

  const renderIcon = () => {
    const sizePx = parseInt(resolveButtonSize(), 10) || 56;
    const iconProps = {
      size: Math.max(16, Math.round(sizePx * 0.43)),
      color: getThemeValue(theme, "buttonColor"),
      className: "voice-button__icon",
    };

    switch (state) {
      case "recording":
        return <IconMicrophoneOff {...iconProps} />;
      case "connecting":
        return <IconLoader {...iconProps} />;
      default:
        return <IconMicrophone {...iconProps} />;
    }
  };

  if (children) {
    return (
      <button
        className={`voice-button voice-button--${state} ${className}`}
        style={getButtonStyles()}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        disabled={state === "disabled"}
        aria-label={getAriaLabel()}
        type="button"
      >
        {children}
      </button>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
      }}
    >
      {/* Voice Button */}
      <button
        className={`voice-button voice-button--${state} ${className}`}
        style={getButtonStyles()}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        disabled={state === "disabled"}
        aria-label={getAriaLabel()}
        type="button"
      >
        {renderIcon()}
      </button>

      {/* Compact Status Display - Only show when active */}
      {(state === "recording" || state === "connecting") && (
        <div
          style={{
            background: getThemeValue(theme, "statusBackground"),
            backdropFilter: "blur(8px)",
            borderRadius: "8px",
            padding: "4px 8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e2e8f0",
            fontSize: "10px",
            color: getThemeValue(theme, "statusColor"),
            fontWeight: "500",
            textAlign: "center",
            whiteSpace: "nowrap",
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            marginTop: "4px",
            fontFamily: getThemeValue(theme, "fontFamily"),
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              justifyContent: "center",
            }}
          >
            <TalkingAvatar
              isRecording={state === "recording"}
              isSpeaking={false}
              size={12}
            />
            {state === "recording" ? t.statusListening : t.statusConnecting}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceButton;
